import { memo, useCallback, useMemo } from 'react'
import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { OverviewWidgetResponse_Field } from '../../proto/generated/overview-widget'
import { Security } from '../../proto/generated/security'
import { indexFieldsByBaseCode } from '../../util/overviewFields'
import useOverviewPanelNavigation from '../../hooks/useOverviewPanelNavigation'
import { OverviewMoreButton } from './OverviewSectionHeader'

const PLEDGE_WARNING_THRESHOLD_PCT = 25

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: theme.spacing(2)
    },
    bar: {
        display: 'flex',
        width: '100%',
        height: 8,
        borderRadius: 4,
        overflow: 'hidden',
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'
    },
    legend: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        marginTop: theme.spacing(1.25)
    },
    legendItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75)
    },
    legendDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0
    },
    legendLabel: {
        fontSize: 11,
        color: theme.palette.text.secondary
    },
    legendValue: {
        fontSize: 11,
        fontWeight: 500,
        fontVariantNumeric: 'tabular-nums'
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1.25)
    },
    asOf: {
        fontSize: 10,
        color: theme.palette.text.disabled,
        textTransform: 'uppercase'
    },
    pledge: {
        fontSize: 11,
        fontWeight: 500
    },
}))

const HOLDING_CODES = [
    { code: 'shareholding_promoter_holding', label: 'Promoter' },
    { code: 'shareholding_fii_holding', label: 'FII' },
    { code: 'shareholding_dii_holding', label: 'DII' },
    { code: 'shareholding_public_holding', label: 'Public' }
] as const

const PLEDGE_CODE = 'shareholding_promoter_holding_pledged_percentage'
const AS_OF_CODE = 'shareholding_as_of_month'

const toPercent = (field: OverviewWidgetResponse_Field | undefined): number | null => {
    if (!field?.value) return null
    const value = Number(field.value)
    return Number.isFinite(value) ? value : null
}

export type ShareholdingSectionProps = {
    fields: OverviewWidgetResponse_Field[]
    security: Security | undefined
}

const ShareholdingSection = ({ fields, security }: ShareholdingSectionProps) => {
    const { classes } = useStyles()
    const theme = useTheme()
    const openShareholdingPanel = useOverviewPanelNavigation('shareholding')

    const handleOpenShareholding = useCallback(() => {
        openShareholdingPanel(security)
    }, [openShareholdingPanel, security])

    const fieldByCode = useMemo(() => indexFieldsByBaseCode(fields), [fields])

    const palette = useMemo(
        () => [
            theme.palette.primary.main,
            theme.palette.success.main,
            theme.palette.warning.main,
            theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[400]
        ],
        [theme]
    )

    const segments = useMemo(
        () =>
            HOLDING_CODES.map((holding, index) => ({
                ...holding,
                value: toPercent(fieldByCode.get(holding.code)),
                color: palette[index]
            })).filter((segment): segment is typeof segment & { value: number } => segment.value !== null && segment.value > 0),
        [fieldByCode, palette]
    )

    const pledge = toPercent(fieldByCode.get(PLEDGE_CODE))
    const asOf = fieldByCode.get(AS_OF_CODE)?.value

    if (segments.length === 0) return null

    const total = segments.reduce((sum, segment) => sum + segment.value, 0)

    return (
        <Box className={classes.container}>
            <Box className={classes.bar}>
                {segments.map((segment) => (
                    <Tooltip key={segment.code} title={`${segment.label} ${segment.value.toFixed(2)}%`} arrow>
                        <Box
                            sx={{
                                width: `${(segment.value / Math.max(total, 100)) * 100}%`,
                                backgroundColor: segment.color
                            }}
                        />
                    </Tooltip>
                ))}
            </Box>

            <Box className={classes.legend}>
                {segments.map((segment) => (
                    <Stack key={segment.code} direction="row" className={classes.legendItem}>
                        <Box className={classes.legendDot} sx={{ backgroundColor: segment.color }} />
                        <Typography className={classes.legendLabel}>{segment.label}</Typography>
                        <Typography className={classes.legendValue}>{segment.value.toFixed(2)}%</Typography>
                    </Stack>
                ))}
            </Box>

            {(pledge !== null || asOf) && (
                <Box className={classes.footer}>
                    {pledge !== null ? (
                        <Typography
                            className={classes.pledge}
                            sx={{
                                color:
                                    pledge >= PLEDGE_WARNING_THRESHOLD_PCT
                                        ? theme.palette.sell.main
                                        : theme.palette.text.secondary
                            }}
                        >
                            {pledge > 0 ? `${pledge.toFixed(2)}% promoter holding pledged` : 'No promoter pledging'}
                        </Typography>
                    ) : (
                        <span />
                    )}
                    {asOf && <Typography className={classes.asOf}>{asOf}</Typography>}
                </Box>
            )}

            <OverviewMoreButton label="More shareholding" onClick={handleOpenShareholding} />
        </Box>
    )
}

export default memo(ShareholdingSection)
